import clsx from "clsx";
import { Tag } from "@chef/components";
import { CurrencyWrapper as Currency } from "@chef/smart-components";
import { Discount, Gift } from "@chef/icons/large";
import { formatDate } from "@chef/helpers";
import { CREDIT_TYPE_IDS } from "@chef/constants";
import { BillingQuery, UserCreditsQuery } from "@chef/state-management";
import { Credit } from "@chef/icons/small";
import { isEqualStrings } from "@chef/utils/equal";
import { intl } from "./VoucherCard.Intl";

interface VoucherCardProps {
  discount?: BillingQuery["billing"]["discounts"][number];
  giftcard?: BillingQuery["billing"]["giftCards"][number];
  credit?: UserCreditsQuery["userCredits"][number];
  cardType?: "discount" | "giftcard" | "credit";
  status?: "upcoming" | "active" | "expired" | "used";
  id?: string;
}

const DiscountDetails = ({
  discount,
  status,
}: Pick<VoucherCardProps, "discount" | "status">) => {
  const isExpired = status === "expired";
  const isUsed = status === "used";

  if (!discount) {
    return null;
  }

  const isDiscountPermanent = !!discount && discount.usesLeft < 0;

  const temporaryDiscountUsesLeft = `${discount.usesLeft} ${
    discount.usesLeft === 1 ? intl.DELIVERY : intl.DELIVERIES
  }`;

  return (
    <table className={clsx("mt-2", (isExpired || isUsed) && "text-grey-1")}>
      <tr>
        <td className="pr-4">{intl.NAME}:</td>
        <td>
          <strong>{discount.name}</strong>
        </td>
      </tr>

      <tr>
        <td className="pr-4">{intl.DISCOUNT}:</td>
        <td>
          <strong>
            {discount.amount}
            {discount.type}
          </strong>
        </td>
      </tr>

      <tr>
        <td className="pr-4">{intl.APPLIES}:</td>
        <td>
          <strong>
            {isDiscountPermanent ? intl.PERMANENT : temporaryDiscountUsesLeft}
          </strong>
        </td>
      </tr>

      <tr>
        <td className="pr-4">{intl.EXPIRES}:</td>
        <td>
          <strong className={clsx((isExpired || isUsed) && "text-error")}>
            {discount && formatDate(discount.validEndDate, "d. MMM yyyy")}
          </strong>
        </td>
      </tr>
    </table>
  );
};

const GiftcardDetails = ({
  giftcard,
  status,
}: Pick<VoucherCardProps, "giftcard" | "status">) => {
  const isExpired = status === "expired";

  if (!giftcard) {
    return null;
  }

  return (
    <table className={clsx("mt-2", isExpired && "text-grey-1")}>
      <tr>
        <td className="pr-4">{intl.NAME}:</td>
        <td>
          <strong>{giftcard.giftCode}</strong>
        </td>
      </tr>
      <tr>
        <td className="pr-4">{intl.AMOUNT}:</td>
        <td>
          <strong>
            <Currency>{+giftcard.totalAmount}</Currency>
          </strong>
        </td>
      </tr>
      <tr>
        <td className="pr-4">{intl.REMAINING_AMOUNT}:</td>
        <td>
          <strong>
            <Currency>{+giftcard.remainingAmount}</Currency>
          </strong>
        </td>
      </tr>
      <tr>
        <td className="pr-4">{intl.EXPIRES}:</td>
        <td>
          <strong className={clsx(isExpired && "text-error")}>
            {giftcard && formatDate(giftcard.expirationDate, "d. MMM yyyy")}
          </strong>
        </td>
      </tr>
    </table>
  );
};

const CreditDetails = ({
  credit,
  status,
}: Pick<VoucherCardProps, "credit" | "status">) => {
  const isExpired = status === "expired";
  const isUsed = status === "used";
  if (!credit) {
    return null;
  }

  let creditTypeName: string;

  switch (credit.creditTypeId.toUpperCase()) {
    case CREDIT_TYPE_IDS.DEBT_TO_CUSTOMER_EXTRA_PAYMENT:
      creditTypeName = intl.CUSTOMER_SERVICE;
      break;
    case CREDIT_TYPE_IDS.GIFT_CARD_CREDIT:
      creditTypeName = intl.GIFTCARD;
      break;
    default:
      creditTypeName = intl.CREDIT;
  }

  return (
    <table className="mt-2">
      <tr>
        <td className="pr-4">{intl.NAME}:</td>
        <td>
          <strong>{intl.REMAINING_CREDIT}</strong>
        </td>
      </tr>

      <tr>
        <td className="pr-4">{intl.REMAINING_AMOUNT}:</td>
        <td>
          <strong>
            <Currency>{credit.remainingAmount}</Currency>
          </strong>
        </td>
      </tr>

      <tr>
        <td className="pr-4">{intl.REFERENCE}:</td>
        <td>
          <strong>{creditTypeName}</strong>
        </td>
      </tr>

      {credit.expirationDate && (
        <tr>
          <td className="pr-4">{intl.EXPIRES}:</td>
          <td>
            <strong className={clsx((isExpired || isUsed) && "text-error")}>
              {credit && formatDate(credit.expirationDate, "d. MMM yyyy")}
            </strong>
          </td>
        </tr>
      )}
    </table>
  );
};

export const VoucherCard = ({
  discount,
  giftcard,
  credit,
  status = "upcoming",
  cardType = "discount",
  id,
  ...props
}: VoucherCardProps) => {
  const discountStatusText = {
    upcoming: intl.UPCOMING,
    active: intl.ACTIVE,
    expired: intl.EXPIRED,
    used: intl.USED,
  };

  const isExpired = status === "expired";
  const isUsed = status === "used";

  // We don't want to show gift card credits in the my account page
  // since we display these as separate "gift card" entities
  if (
    cardType === "credit" &&
    isEqualStrings(credit?.creditTypeId, CREDIT_TYPE_IDS.GIFT_CARD_CREDIT)
  ) {
    return null;
  }

  return (
    <section
      className={clsx(
        "p-6 my-4",
        (status === "upcoming" || status === "expired" || status === "used") &&
          "bg-white shadow",
        status === "active" &&
          "bg-highlightBG border-highlight border-dotted border-4",
      )}
      id={id}
      {...props}
    >
      <div className="flex items-center justify-between">
        <div className={clsx((isExpired || isUsed) && "text-grey-1")}>
          {cardType === "discount" && (
            <>
              <Discount className="w-6 h-6" />
              <strong className="ml-4">{intl.DISCOUNT_CODE}</strong>
            </>
          )}
          {cardType === "giftcard" && (
            <>
              <Gift className="text-lg" />
              <strong className="ml-4">{intl.GIFTCARD_CODE}</strong>
            </>
          )}
          {cardType === "credit" && (
            <>
              <Credit className="text-lg" />
              <strong className="ml-4">{intl.CREDIT}</strong>
            </>
          )}
        </div>
        <Tag
          highlight={status === "active"}
          className={clsx((isExpired || isUsed) && "!bg-error text-white")}
        >
          <strong>{discountStatusText[status]}</strong>
        </Tag>
      </div>
      {cardType === "discount" && (
        <DiscountDetails discount={discount} status={status} />
      )}
      {cardType === "giftcard" && (
        <GiftcardDetails giftcard={giftcard} status={status} />
      )}
      {cardType === "credit" && <CreditDetails credit={credit} />}
    </section>
  );
};
